import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 6) {
            setError("La password deve avere almeno 6 caratteri.");
            return;
        }
        if (password !== confirm) {
            setError("Le password non coincidono.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            navigate("/login?reset=ok");
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    if (!token) {
        return (
            <div
                className="min-h-screen flex items-center justify-center bg-sf-bg p-8"
                data-testid="reset-password-page-invalid"
            >
                <div className="max-w-sm text-center">
                    <Logo className="h-24 w-auto mx-auto mb-8" />
                    <h1 className="font-display text-3xl text-navy mb-3">
                        Link non valido
                    </h1>
                    <p className="text-sf-secondary mb-6">
                        Il link che hai usato non contiene un token valido.
                        Richiedi un nuovo reset.
                    </p>
                    <Link to="/forgot-password">
                        <Button className="bg-navy text-white">
                            Richiedi nuovo link
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen flex items-center justify-center bg-sf-bg p-8"
            data-testid="reset-password-page"
        >
            <div className="w-full max-w-sm">
                <Link to="/">
                    <Logo className="h-24 w-auto mb-10" />
                </Link>
                <div className="label-eyebrow mb-3">Nuova password</div>
                <h1 className="font-display text-4xl text-navy mb-3">
                    Imposta la nuova password
                </h1>
                <p className="text-sf-secondary mb-8 text-sm">
                    Scegli una nuova password sicura (minimo 6 caratteri).
                </p>

                <form onSubmit={submit} data-testid="reset-form">
                    {error && (
                        <div
                            className="mb-4 p-3 text-sm bg-red-50 border border-red-200 text-red-700 rounded-md"
                            data-testid="reset-error"
                        >
                            {error}
                        </div>
                    )}
                    <div className="space-y-4 mb-6">
                        <div>
                            <Label htmlFor="password">Nuova password</Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={6}
                                className="mt-2"
                                data-testid="reset-password"
                            />
                        </div>
                        <div>
                            <Label htmlFor="confirm">Conferma password</Label>
                            <Input
                                id="confirm"
                                type="password"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                required
                                minLength={6}
                                className="mt-2"
                                data-testid="reset-confirm"
                            />
                        </div>
                    </div>
                    <Button
                        type="submit"
                        disabled={busy}
                        className="w-full bg-navy hover:bg-navy/90 text-white lift"
                        data-testid="reset-submit"
                    >
                        {busy ? "Aggiornamento…" : "Aggiorna password"}
                    </Button>
                </form>
            </div>
        </div>
    );
}
